import { take } from "lodash";
import { useMemo, useState } from "react";
import { AssessmentType, GroupedAnswersNode } from "types/graphql-schema";

import AssessmentQuestionHeading from "@apps/assessments/components/questions/assessment-question-heading";
import {
  AnswerGroup,
  getQuestionClass,
} from "@apps/assessments/components/questions/question";
import {
  AssessmentAnswerWithResponderAndId,
  AssessmentQuestion,
} from "@apps/assessments/components/questions/types";
import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import ToggleChevronIcon from "@components/layout/toggle-chevron-icon";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import { pluralize } from "@helpers/string";

const MAX_COMMENT_CHUNK_SIZE = 100;

const EngagementSurveyDeliveryQuestion = ({
  answers,
  question,
  isGroupingEnabled = false,
  groupedAnswers,
  answerMap,
}: {
  answers: AssessmentAnswerWithResponderAndId[];
  question: AssessmentQuestion;
  isGroupingEnabled?: boolean;
  groupedAnswers?: ReadonlyArray<GroupedAnswersNode | null> | null;
  answerMap?: Map<number, AssessmentAnswerWithResponderAndId>;
}) => {
  const label = useLabel();
  const [commentLimit, setCommentLimit] = useState(MAX_COMMENT_CHUNK_SIZE);
  const [collapsedComments, setCollapsedComments] = useState(true);
  const QuestionClass = getQuestionClass(question, { label });

  const answersWithComment = useMemo(
    () => answers.filter((answer) => !isEmptyValue(answer.comment)),
    [answers],
  );

  // Build answer groups if grouping is enabled
  const answerGroups: AnswerGroup[] | null = useMemo(() => {
    if (!isGroupingEnabled || !groupedAnswers || !answerMap) {
      return null;
    }

    return groupedAnswers
      .filter((group): group is GroupedAnswersNode => group !== null)
      .filter((group) => !group.isCollapsed)
      .map((group) => {
        const groupAnswerObjects = group.answerIds
          .map((id) => (id != null ? answerMap.get(id) : undefined))
          .filter(Boolean) as AssessmentAnswerWithResponderAndId[];

        return {
          groupKey: group.groupKey,
          groupLabel: group.groupLabel,
          answerCount: group.numberResponders,
          answersWithCommentsCount: group.numberComments,
          answers: groupAnswerObjects,
          isHidden: group.isHidden,
        };
      })
      .sort((a, b) => {
        if (a.groupLabel === "Other") return 1;
        if (b.groupLabel === "Other") return -1;
        if (a.groupLabel === "Uncategorized") return 1;
        if (b.groupLabel === "Uncategorized") return -1;
        return b.answerCount - a.answerCount;
      });
  }, [isGroupingEnabled, groupedAnswers, answerMap]);

  const commentContent = useMemo(
    () =>
      QuestionClass.getDeliveryComments({
        answers: take(answersWithComment, commentLimit),
        answerGroups,
      }),
    [QuestionClass, answersWithComment, commentLimit, answerGroups],
  );

  if (answers.length === 0) {
    return null;
  }

  return (
    <div
      key={question.id}
      className="border rounded-lg"
      aria-label={`Engagement survey delivery question: ${question.title}`}
    >
      <div className="px-6 pt-6 flex items-baseline justify-between">
        <div className="pr-12">
          <AssessmentQuestionHeading
            question={question}
            assessmentType={AssessmentType.EngagementSurvey}
          />
        </div>
        <div className="shrink-0 text-sm text-gray-500">
          {answers.length} {pluralize("responder", answers.length)}
        </div>
      </div>

      <div className="mt-4 px-6 pb-6">
        {QuestionClass.getEngagementSurveyDelivery({ answers, answerGroups })}
      </div>

      {commentContent && (
        <div className="border-t p-6 flex flex-col gap-4">
          <div
            className="px-2 py-1 -mx-2 rounded hover:bg-gray-50"
            role="button"
            onClick={() => setCollapsedComments(!collapsedComments)}
          >
            <div className="flex items-center gap-2 font-semibold">
              <div className="text-sm text-gray-900">Comments</div>
              <div className="text-xs py-0.5 px-2 bg-gray-100 rounded-md">
                {answersWithComment.length}
              </div>
              <ToggleChevronIcon expanded={!collapsedComments} />
            </div>
          </div>
          {!collapsedComments && commentContent}
          {!collapsedComments && commentLimit < answersWithComment.length && (
            <div className="mt-6">
              <Button
                theme={buttonTheme.lightGray}
                onClick={() =>
                  setCommentLimit(commentLimit + MAX_COMMENT_CHUNK_SIZE)
                }
                text="View More"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EngagementSurveyDeliveryQuestion;
